import React, { Suspense } from "react";
import { IconButton, Stack } from "@mui/material";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import { DrawerHeader } from "@jumbo/components/JumboLayout/style";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useJumboLayoutHeader from "@jumbo/hooks/useJumboLayoutHeader";
import useJumboSidebarTheme from "@jumbo/hooks/useJumboSidebarTheme";
import { SIDEBAR_STYLES, SIDEBAR_VIEWS } from "@jumbo/utils/constants/layout";
import Logo from "../../../../shared/Logo";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Zoom from "@mui/material/Zoom";
import Div from "@jumbo/shared/Div";
import SidebarSkeleton from "./SidebarSkeleton";
import LanguageSelector from "app/shared/widgets/LanguageSelector/LanguageSelector";
import AuthUserDropdown from "app/shared/widgets/AuthUserDropdown";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import PeopleIcon from "@mui/icons-material/People";
import usePartnerAuth from "@jumbo/hooks/usePartnerAuth";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { resolveSidebarMenuList } from "app/navigation/sidebar";

const Sidebar = () => {
  const { getAuthUser } = useJumboAuth();
  const { user: partnerUser } = usePartnerAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { customer, type, userId } = getAuthUser() || {};
  const dsokSettings = customer?.dsokSettings;
  const PARTNER_TOKEN = localStorage.getItem("PARTNER_TOKEN");
  if (!PARTNER_TOKEN) {
    localStorage.setItem("dsokSettings", JSON.stringify(dsokSettings));
  }

  const menuList = resolveSidebarMenuList({
    customer,
    type,
    userId,
    location,
    searchParams,
    partnerUser,
  });

  return (
    <React.Fragment>
      <SidebarHeader />
      <JumboScrollbar autoHide autoHideDuration={200} autoHideTimeout={500}>
        <Suspense
          fallback={
            <Div
              sx={{
                display: "flex",
                minWidth: 0,
                alignItems: "center",
                alignContent: "center",
                px: 3,
              }}
            >
              <SidebarSkeleton />
            </Div>
          }
        >
          <JumboVerticalNavbar translate items={menuList} />
        </Suspense>
      </JumboScrollbar>
      <Div
        sx={{
          display: "flex",
          justifyContent: "center",
          borderTop: "solid 1px",
          borderColor: "divider",
          marginX: "2px",
        }}
      >
        <Stack direction="column" alignItems="center" sx={{ ml: "auto" }}>
          <Suspense fallback={<></>}>
            <LanguageSelector align="center" />
          </Suspense>
          <AuthUserDropdown />
        </Stack>
      </Div>
    </React.Fragment>
  );
};

const SidebarHeader = () => {
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const { setHeaderOptions } = useJumboLayoutHeader();
  const { sidebarTheme } = useJumboSidebarTheme();

  const isMiniAndClosed = React.useMemo(() => {
    return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
  }, [sidebarOptions.view, sidebarOptions.open]);

  const handleOpen = () => {
    setSidebarOptions({ open: false });
    setHeaderOptions({ hide: false });
  };

  return (
    <React.Fragment>
      {sidebarOptions?.style !== SIDEBAR_STYLES.CLIPPED_UNDER_HEADER && (
        <DrawerHeader>
          <Link
            to="/"
            style={{
              display: "inline-flex",
              textDecoration: "none",
              cursor: "pointer",
            }}
            aria-label="DataVision – domov"
          >
            <Logo mini={isMiniAndClosed} mode={sidebarTheme.type} />
          </Link>
          {sidebarOptions?.view !== SIDEBAR_VIEWS.MINI && (
            <Zoom in={sidebarOptions?.open}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ ml: 0, mr: -1.5 }}
                onClick={handleOpen}
              >
                <MenuOpenIcon />
              </IconButton>
            </Zoom>
          )}
        </DrawerHeader>
      )}
    </React.Fragment>
  );
};

export default Sidebar;
